import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  MapPin,
  MoreHorizontal,
  Store,
  Truck,
  Receipt,
  Users,
  Database,
  CheckCircle,
  LogOut,
  ChevronRight,
  X,
  FileText,
  User,
  Clock,
  Smartphone
} from 'lucide-react';
import { cn } from '../lib/utils';
import useAttendanceGate from '../hooks/useAttendanceGate';
import useRouteTracker from '../hooks/useRouteTracker';

const LOGO_URL = "https://shopkeventer.com/cdn/shop/files/keventer-logo-removebg-preview_260x.png?v=1648031487";

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  // Trace the road while the day is open. Mounted here so it keeps running as
  // the user moves between screens - admins never punch, so they never track.
  const { allowed: dayOpen } = useAttendanceGate();
  useRouteTracker(!isAdmin && dayOpen);
  const [showMore, setShowMore] = React.useState(false);

  // Orders and Damages are withdrawn in this pouch-milk-only version (KEYNOTES 3, 4).
  // Attendance takes the third slot because punch in / punch out is now mandatory daily.
  const mainNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/visits', icon: MapPin, label: 'Visit' },
    { path: '/attendance', icon: Clock, label: 'Attendance' },
  ];

  const moreMenuItems = [
    { path: '/distributors', icon: Truck, label: 'Distributors' },
    { path: '/outlets', icon: Store, label: 'Outlets' },
    { path: '/expenses', icon: Receipt, label: 'TADA/Expenses' },
    { path: '/reports', icon: FileText, label: 'Reports' },
    { path: '/profile', icon: User, label: 'My Profile' },
    ...(isAdmin ? [
      { path: '/masters', icon: Database, label: 'Masters' },
      { path: '/users', icon: Users, label: 'Users' },
      { path: '/approvals', icon: CheckCircle, label: 'Approvals' },
      { path: '/device-alerts', icon: Smartphone, label: 'Device Alerts' },
    ] : []),
  ];

  const isMoreActive = moreMenuItems.some(item => location.pathname.startsWith(item.path));

  return (
    <div className="min-h-screen bg-slate-50 pb-20 md:pb-0 md:pl-64">
      {/* Desktop Sidebar - Light Theme */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 flex-col z-50 shadow-sm">
        <div className="p-6 border-b border-slate-100">
          <img src={LOGO_URL} alt="Keventer" className="h-10 object-contain" />
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {mainNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all",
                  isActive 
                    ? "bg-sky-600 text-white shadow-md shadow-sky-200" 
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                )
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
          
          <div className="pt-4 mt-4 border-t border-slate-100">
            <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
              More Options
            </p>
            {moreMenuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all",
                    isActive 
                      ? "bg-sky-600 text-white shadow-md shadow-sky-200" 
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  )
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <button 
              onClick={logout}
              className="p-2 text-slate-400 hover:text-red-500 transition-colors"
              data-testid="sidebar-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-slate-200 z-40 h-14 flex items-center px-4">
        <img src={LOGO_URL} alt="Keventer" className="h-8 object-contain" />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700">{user?.name}</span>
          <div className="w-8 h-8 rounded-full bg-sky-600 flex items-center justify-center text-white text-sm font-semibold">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-14 md:pt-0 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-200 z-50 h-16 flex items-center justify-around bottom-nav pb-safe">
        {mainNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center justify-center gap-1 px-4 py-2 rounded-xl transition-all",
                isActive 
                  ? "text-sky-600" 
                  : "text-slate-400"
              )
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs font-medium">{item.label}</span>
          </NavLink>
        ))}
        <button
          onClick={() => setShowMore(true)}
          data-testid="nav-more"
          className={cn(
            "flex flex-col items-center justify-center gap-1 px-4 py-2 rounded-xl transition-all",
            isMoreActive ? "text-sky-600" : "text-slate-400"
          )}
        >
          <MoreHorizontal className="w-5 h-5" />
          <span className="text-xs font-medium">More</span>
        </button>
      </nav>

      {/* More Menu Modal */}
      {showMore && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-[60] flex items-end">
          <div 
            className="absolute inset-0" 
            onClick={() => setShowMore(false)}
          />
          <div className="relative w-full bg-white rounded-t-3xl p-6 pb-safe slide-up max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-900">More Options</h2>
              <button 
                onClick={() => setShowMore(false)}
                className="p-2 rounded-full hover:bg-slate-100"
                data-testid="close-more-menu"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="space-y-2">
              {moreMenuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setShowMore(false)}
                  data-testid={`more-${item.label.toLowerCase().replace(/\//g, '-')}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center justify-between px-4 py-4 rounded-xl transition-all",
                      isActive 
                        ? "bg-sky-50 text-sky-700" 
                        : "text-slate-700 hover:bg-slate-50"
                    )
                  }
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      location.pathname.startsWith(item.path) ? "bg-sky-100" : "bg-slate-100"
                    )}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </NavLink>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <button
                onClick={() => { logout(); setShowMore(false); }}
                data-testid="mobile-logout-btn"
                className="flex items-center gap-3 w-full px-4 py-4 rounded-xl text-red-600 hover:bg-red-50 transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <LogOut className="w-5 h-5" />
                </div>
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
